import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, RotateCcw, Info, Shield, ShieldAlert, ShieldCheck, ChevronRight, Send, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  PlaygroundState,
  LevelSettings,
  NsfwPresetLevel,
  AccuracyLevel,
  ACCURACY_LABELS,
  getDefaultPlaygroundState,
  getEffectiveRuleText,
  generateLevelJson,
  DEFAULT_LIGHT_SETTINGS,
  DEFAULT_MEDIUM_SETTINGS,
  DEFAULT_STRICT_SETTINGS,
} from "@/config/browserSettingsDefaults";
import {
  TEXT_FILTER_CATEGORIES,
  TextFilterCategory,
  presetToThresholdLevel,
} from "@/config/textFilterCategories";

// Helper component for threshold sliders
const ThresholdSlider = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label className="text-sm">{label}</Label>
      <span className="text-sm font-mono text-muted-foreground">
        {(value * 100).toFixed(0)}%
      </span>
    </div>
    <Slider
      value={[value]}
      min={0}
      max={1}
      step={0.05}
      onValueChange={([v]) => onChange(v)}
      className="w-full"
    />
  </div>
);

// Text Filter Category Row Component - Backend format: [redMark, urlRedMark]
const TextFilterCategoryRow = ({
  category,
  enabled,
  redMark,
  urlRedMark,
  defaultRedMark,
  defaultUrlRedMark,
  onToggle,
  onRedMarkChange,
  onUrlRedMarkChange,
}: {
  category: TextFilterCategory;
  enabled: boolean;
  redMark: number;
  urlRedMark: number;
  defaultRedMark: number;
  defaultUrlRedMark: number;
  onToggle: (enabled: boolean) => void;
  onRedMarkChange: (value: number) => void;
  onUrlRedMarkChange: (value: number) => void;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-lg border ${enabled ? "" : "opacity-50"}`}>
      <div className="flex items-center gap-4 p-3">
        <Switch
          checked={enabled}
          disabled={category.alwaysOn}
          onCheckedChange={onToggle}
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{category.displayName}</span>
            {category.alwaysOn && (
              <Badge variant="secondary" className="text-xs">Always On</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-mono">
            Thresholds: [{redMark}, {urlRedMark}]
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          disabled={!enabled}
        >
          <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </Button>
      </div>
      
      {expanded && enabled && (
        <div className="border-t px-3 py-3 space-y-3 bg-muted/30">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">redMark Threshold</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="10"
                value={redMark}
                onChange={(e) => onRedMarkChange(parseFloat(e.target.value) || defaultRedMark)}
                className="h-8"
              />
              <p className="text-xs text-muted-foreground">Default: {defaultRedMark}</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">urlRedMark Threshold</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="10"
                value={urlRedMark}
                onChange={(e) => onUrlRedMarkChange(parseFloat(e.target.value) || defaultUrlRedMark)}
                className="h-8"
              />
              <p className="text-xs text-muted-foreground">Default: {defaultUrlRedMark}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// NSFW Explanation Panel - collapsible reference for admins
const NsfwExplanationPanel = () => (
  <Accordion type="single" collapsible className="border rounded-lg bg-card">
    <AccordionItem value="nsfw-explanation" className="border-0">
      <AccordionTrigger className="px-4 py-3 text-sm font-semibold hover:no-underline">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-muted-foreground" />
          How NSFW Filtering Works
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <div className="space-y-6 text-sm">

          {/* 1. Classification Model */}
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">1. The Classification Model</h4>
            <p className="text-muted-foreground">
              The system uses an on-device Vision CoreML model that classifies every image into five categories.
              For each image, the model outputs a confidence score between 0.0 and 1.0, and all scores sum to approximately 1.0.
            </p>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {[
                { code: "P", label: "Porn", desc: "Explicit pornographic content" },
                { code: "S", label: "Sexy", desc: "Suggestive or sexualized content" },
                { code: "H", label: "Hentai", desc: "Animated/drawn explicit content" },
                { code: "N", label: "Neutral", desc: "Safe, non-sexual content" },
                { code: "D", label: "Drawings", desc: "Non-sexual illustrations" },
              ].map(({ code, label, desc }) => (
                <div key={code} className="rounded-md border p-2 text-center space-y-1">
                  <span className="text-base font-bold font-mono text-foreground">{code}</span>
                  <div className="text-xs font-medium">{label}</div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                </div>
              ))}
            </div>
            <div className="rounded-md bg-muted/50 border p-3 mt-2 font-mono text-xs space-y-1">
              <p className="text-muted-foreground mb-1">Example output — slightly suggestive image:</p>
              <p>neutral: <span className="text-foreground">0.55</span></p>
              <p>sexy: <span className="text-foreground">0.30</span></p>
              <p>porn: <span className="text-foreground">0.05</span></p>
              <p>hentai: <span className="text-foreground">0.02</span></p>
              <p>drawings: <span className="text-foreground">0.08</span></p>
            </div>
          </div>

          {/* 2. What Thresholds Mean */}
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">2. What Thresholds Mean</h4>
            <p className="text-muted-foreground">
              Each threshold defines the <strong>minimum confidence</strong> required to block an image for a specific category.
            </p>
            <ul className="space-y-1 text-muted-foreground list-none">
              <li>↓ <strong className="text-foreground">Lower threshold</strong> → stricter filtering (blocks at lower confidence)</li>
              <li>↑ <strong className="text-foreground">Higher threshold</strong> → more lenient (requires higher confidence to block)</li>
              <li>= <strong className="text-foreground">1.0</strong> → effectively disabled (model rarely outputs exactly 1.0)</li>
            </ul>
          </div>

          {/* 3. The Three Presets */}
          <div className="space-y-3">
            <h4 className="font-semibold text-foreground">3. The Three Presets</h4>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: "#17c483", color: "#fff", borderColor: "transparent" }}>Light</span>
                </div>
                <p className="text-xs text-muted-foreground">Blocks only highly confident porn/hentai. Does not block "sexy" alone. Uses combo detection for borderline cases. Neutral rule disabled.</p>
                <p className="text-xs font-medium">Best for: minimal filtering with only obvious explicit content blocked.</p>
              </div>
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: "#fdc557", color: "#232852", borderColor: "transparent" }}>Medium</span>
                </div>
                <p className="text-xs text-muted-foreground">Blocks porn, sexy, or hentai at moderate confidence (60%+). Uses combo detection. Neutral rule disabled.</p>
                <p className="text-xs font-medium">Best for: general-purpose filtering with a reasonable balance.</p>
              </div>
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: "#fd66a0", color: "#fff", borderColor: "transparent" }}>Strict</span>
                </div>
                <p className="text-xs text-muted-foreground">Blocks porn at 40%+, sexy at 30%+, hentai at 40%+. Enables combo detection at very low thresholds. Activates neutral-based borderline rule.</p>
                <p className="text-xs font-medium">Best for: high-safety environments (schools, enterprise, parental control).</p>
              </div>
            </div>
          </div>

          {/* 4. Evaluation Order */}
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">4. Evaluation Order</h4>
            <ol className="space-y-2 text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-none font-mono font-bold text-foreground w-5">1.</span>
                <div><strong className="text-foreground">Individual Category Checks</strong> — If any single category (P, S, H, D) exceeds its threshold, the image is blocked immediately.</div>
              </li>
              <li className="flex gap-3">
                <span className="flex-none font-mono font-bold text-foreground w-5">2.</span>
                <div><strong className="text-foreground">Combo Checks</strong> — If two related categories are both moderately high (e.g. Porn + Sexy, or Hentai + Sexy), the image is blocked even if neither exceeds its individual threshold.</div>
              </li>
              <li className="flex gap-3">
                <span className="flex-none font-mono font-bold text-foreground w-5">3.</span>
                <div><strong className="text-foreground">Neutral-Based Rule</strong> (Strict mode only) — If Sexy is moderately present and Neutral confidence is not strong, the image is blocked. Acts as an "uncertain = block" safety mechanism.</div>
              </li>
            </ol>
          </div>

          {/* 5. Practical Tuning Guide */}
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">5. Practical Tuning Guide</h4>
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-foreground">Goal</th>
                    <th className="text-left px-3 py-2 font-semibold text-foreground">What to Adjust</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[
                    ["Block more porn", "Lower porn threshold"],
                    ["Block bikini/suggestive images", "Lower sexy threshold"],
                    ["Block animated NSFW", "Lower hentai threshold"],
                    ["Catch borderline images", "Lower combo thresholds"],
                    ["Enable uncertain = block", "Raise neutral threshold"],
                    ["Avoid blocking drawings", "Keep drawings at 1.0"],
                    ["Reduce false positives", "Raise the thresholds causing them"],
                  ].map(([goal, adjust]) => (
                    <tr key={goal} className="hover:bg-muted/30">
                      <td className="px-3 py-2 text-muted-foreground">{goal}</td>
                      <td className="px-3 py-2 font-medium">{adjust}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 6. Warning */}
          <div className="flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800">
            <AlertTriangle className="h-4 w-4 flex-none mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Warning: False Positives at Low Thresholds</p>
              <p className="text-xs">Setting thresholds too low (below ~0.15) will cause many false positives, including normal photos of people, product images, beach scenes, and fitness photos. The strict preset (0.30–0.40 range) is already highly aggressive.</p>
            </div>
          </div>

        </div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

export const BrowserSettingsSection = () => {
  const [state, setState] = useState<PlaygroundState>(getDefaultPlaygroundState());
  const [activeLevel, setActiveLevel] = useState<NsfwPresetLevel>("light");
  const [targetSerial, setTargetSerial] = useState<string>("");
  const [isSending, setIsSending] = useState(false);
  // Text filter state using backend format: redMark + urlRedMark per category
  const [textFilterState, setTextFilterState] = useState<{
    [level: string]: {
      [category: string]: {
        enabled: boolean;
        redMark: number;
        urlRedMark: number;
      };
    };
  }>({
    light: {},
    medium: {},
    strict: {},
  });

  const updateDevicePolicies = useMutation(api.devices.policies.update);
  
  // Look up device by serial number
  const deviceLookup = useQuery(
    api.devices.list.all,
    targetSerial.trim() ? { paginationOpts: { cursor: null, numItems: 10 }, query: targetSerial.trim() } : "skip"
  );
  
  // Find matching device from results
  const matchedDevice = deviceLookup?.page?.find(
    d => d.serial?.toLowerCase() === targetSerial.trim().toLowerCase()
  );

  const updateLevelSetting = <K extends keyof LevelSettings>(
    level: NsfwPresetLevel,
    key: K,
    value: LevelSettings[K]
  ) => {
    setState((prev) => ({
      ...prev,
      levels: {
        ...prev.levels,
        [level]: {
          ...prev.levels[level],
          [key]: value,
        },
      },
    }));
  };

  const updateThreshold = (
    level: NsfwPresetLevel,
    key: keyof LevelSettings["thresholds"],
    value: number
  ) => {
    setState((prev) => ({
      ...prev,
      levels: {
        ...prev.levels,
        [level]: {
          ...prev.levels[level],
          thresholds: {
            ...prev.levels[level].thresholds,
            [key]: value,
          },
        },
      },
    }));
  };

  const updateTextFilterCategory = (
    level: NsfwPresetLevel,
    categoryName: string,
    enabled: boolean
  ) => {
    const defaults = state.levels[level].textFilter[categoryName];
    setTextFilterState(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [categoryName]: {
          enabled,
          redMark: prev[level]?.[categoryName]?.redMark ?? defaults?.redMark ?? 3.0,
          urlRedMark: prev[level]?.[categoryName]?.urlRedMark ?? defaults?.urlRedMark ?? 2.0,
        },
      },
    }));
  };

  const updateTextFilterRedMark = (
    level: NsfwPresetLevel,
    categoryName: string,
    value: number
  ) => {
    const defaults = state.levels[level].textFilter[categoryName];
    setTextFilterState(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [categoryName]: {
          enabled: prev[level]?.[categoryName]?.enabled ?? true,
          redMark: value,
          urlRedMark: prev[level]?.[categoryName]?.urlRedMark ?? defaults?.urlRedMark ?? 2.0,
        },
      },
    }));
  };

  const updateTextFilterUrlRedMark = (
    level: NsfwPresetLevel,
    categoryName: string,
    value: number
  ) => {
    const defaults = state.levels[level].textFilter[categoryName];
    setTextFilterState(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [categoryName]: {
          enabled: prev[level]?.[categoryName]?.enabled ?? true,
          redMark: prev[level]?.[categoryName]?.redMark ?? defaults?.redMark ?? 3.0,
          urlRedMark: value,
        },
      },
    }));
  };

  const isCategoryEnabled = (level: NsfwPresetLevel, category: TextFilterCategory) => {
    if (category.alwaysOn) return true;
    return textFilterState[level]?.[category.name]?.enabled ?? false;
  };

  const getCategoryRedMark = (level: NsfwPresetLevel, categoryName: string): number => {
    const customValue = textFilterState[level]?.[categoryName]?.redMark;
    if (customValue !== undefined) return customValue;
    const defaultSettings = state.levels[level].textFilter[categoryName];
    return defaultSettings?.redMark ?? 3.0;
  };

  const getCategoryUrlRedMark = (level: NsfwPresetLevel, categoryName: string): number => {
    const customValue = textFilterState[level]?.[categoryName]?.urlRedMark;
    if (customValue !== undefined) return customValue;
    const defaultSettings = state.levels[level].textFilter[categoryName];
    return defaultSettings?.urlRedMark ?? 2.0;
  };

  const getDefaultRedMark = (level: NsfwPresetLevel, categoryName: string): number => {
    const defaultSettings = state.levels[level].textFilter[categoryName];
    return defaultSettings?.redMark ?? 3.0;
  };

  const getDefaultUrlRedMark = (level: NsfwPresetLevel, categoryName: string): number => {
    const defaultSettings = state.levels[level].textFilter[categoryName];
    return defaultSettings?.urlRedMark ?? 2.0;
  };

  const resetLevel = (level: NsfwPresetLevel) => {
    const defaults = {
      light: DEFAULT_LIGHT_SETTINGS,
      medium: DEFAULT_MEDIUM_SETTINGS,
      strict: DEFAULT_STRICT_SETTINGS,
    };
    setState((prev) => ({
      ...prev,
      levels: {
        ...prev.levels,
        [level]: JSON.parse(JSON.stringify(defaults[level])),
      },
    }));
    setTextFilterState(prev => ({
      ...prev,
      [level]: {},
    }));
    toast.success(`${level.charAt(0).toUpperCase() + level.slice(1)} level reset to defaults`);
  };

  const resetAll = () => {
    setState(getDefaultPlaygroundState());
    setTextFilterState({ light: {}, medium: {}, strict: {} });
    toast.success("All settings reset to defaults");
  };

  const generateFullJson = (level: NsfwPresetLevel) => {
    const baseJson = generateLevelJson(state.shared, state.levels[level]);
    
    // Build text filter config in backend format: { "Category": [redMark, urlRedMark] }
    const textFilter: Record<string, [number, number]> = {};
    
    TEXT_FILTER_CATEGORIES.forEach(category => {
      if (isCategoryEnabled(level, category)) {
        // Use UI state values (which fall back to defaults if not set)
        const redMark = getCategoryRedMark(level, category.name);
        const urlRedMark = getCategoryUrlRedMark(level, category.name);
        textFilter[category.name] = [redMark, urlRedMark];
      }
    });
    
    // Return in the exact backend format
    return {
      categories: baseJson.categories,
      allowlist: baseJson.allowlist,
      blocklist: baseJson.blocklist,
      config: {
        ...baseJson.config,
        textFilter,
      },
    };
  };

  const copyJson = (level: NsfwPresetLevel) => {
    const json = generateFullJson(level);
    navigator.clipboard.writeText(JSON.stringify(json, null, 2));
    toast.success(`${level.charAt(0).toUpperCase() + level.slice(1)} JSON copied to clipboard`);
  };

  const copyAllPresets = () => {
    const allPresets = {
      light: generateFullJson("light"),
      medium: generateFullJson("medium"),
      strict: generateFullJson("strict"),
    };
    navigator.clipboard.writeText(JSON.stringify(allPresets, null, 2));
    toast.success("All presets copied to clipboard");
  };

  const sendToDevice = async (level: NsfwPresetLevel) => {
    if (!targetSerial.trim()) {
      toast.error("Please enter a serial number");
      return;
    }

    if (!matchedDevice) {
      toast.error("Device not found. Check the serial number.");
      return;
    }

    setIsSending(true);
    try {
      const json = generateFullJson(level);
      
      // Build the blueJayRules object for the backend
      const blueJayRules = {
        categories: json.categories,
        allowlist: json.allowlist,
        blocklist: json.blocklist,
        config: json.config,
      };

      await updateDevicePolicies({
        device: matchedDevice.id,
        blueJayRules,
      });

      toast.success(`${level.charAt(0).toUpperCase() + level.slice(1)} settings sent to ${matchedDevice.serial}`);
    } catch (error) {
      console.error("Error sending to device:", error);
      toast.error("Failed to send settings to device.");
    } finally {
      setIsSending(false);
    }
  };

  const getLevelIcon = (level: NsfwPresetLevel) => {
    switch (level) {
      case "light":
        return <ShieldCheck className="h-4 w-4" />;
      case "medium":
        return <Shield className="h-4 w-4" />;
      case "strict":
        return <ShieldAlert className="h-4 w-4" />;
    }
  };

  const getLevelDescription = (level: NsfwPresetLevel): string => {
    switch (level) {
      case "light":
        return "Minimal filtering - blocks only explicit content";
      case "medium":
        return "Balanced filtering - blocks explicit and suggestive content";
      case "strict":
        return "Maximum filtering - blocks all potentially inappropriate content";
    }
  };

  const renderLevelContent = (level: NsfwPresetLevel) => {
    const settings = state.levels[level];
    const effectiveRules = getEffectiveRuleText(settings.thresholds);
    const thresholdLevel = presetToThresholdLevel(level);

    return (
      <div className="space-y-8">
        {/* Level Header with Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {getLevelIcon(level)}
            <div>
              <h3 className="font-semibold text-xl capitalize">{level} Level</h3>
              <p className="text-sm text-muted-foreground mt-0.5">{getLevelDescription(level)}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => resetLevel(level)}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button variant="outline" onClick={() => copyJson(level)}>
              <Copy className="h-4 w-4 mr-2" />
              Copy JSON
            </Button>
          </div>
        </div>

        {/* Image/Video Filtering Card */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Image & Video Filtering</CardTitle>
            <CardDescription>Control how images and videos are analyzed</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex items-center gap-3">
                  <Label>Block Videos</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Blocks all video content entirely</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Switch
                  checked={settings.blockVideos}
                  onCheckedChange={(checked) => updateLevelSetting(level, "blockVideos", checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border opacity-50">
                <div className="flex items-center gap-3">
                  <Label>Skin Block</Label>
                  <Badge variant="outline" className="text-xs">Disabled</Badge>
                </div>
                <Switch checked={settings.skinBlock} disabled />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex items-center gap-3">
                  <Label>Human Detection</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Enable human body rectangle detection for blurring</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Switch
                  checked={settings.human}
                  onCheckedChange={(checked) => updateLevelSetting(level, "human", checked)}
                />
              </div>

              <div className="p-4 rounded-lg border space-y-3">
                <Label>Accuracy Mode</Label>
                <div className="flex gap-2">
                  {([0, 1, 2] as AccuracyLevel[]).map((acc) => (
                    <Button
                      key={acc}
                      variant={settings.accuracy === acc ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => updateLevelSetting(level, "accuracy", acc)}
                    >
                      {ACCURACY_LABELS[acc]}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* NSFW Thresholds Card */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">NSFW Thresholds</CardTitle>
            <CardDescription>Adjust detection sensitivity for different content types</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Main thresholds */}
            <div className="grid gap-6 md:grid-cols-3">
              <ThresholdSlider
                label="Porn (P)"
                value={settings.thresholds.porn}
                onChange={(v) => updateThreshold(level, "porn", v)}
              />
              <ThresholdSlider
                label="Hentai (H)"
                value={settings.thresholds.hentai}
                onChange={(v) => updateThreshold(level, "hentai", v)}
              />
              <ThresholdSlider
                label="Sexy (S)"
                value={settings.thresholds.sexy}
                onChange={(v) => updateThreshold(level, "sexy", v)}
              />
            </div>

            {/* Combo thresholds */}
            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-4">Combination Rules</p>
              <div className="grid gap-6 md:grid-cols-2">
                <ThresholdSlider
                  label="P + S combo min"
                  value={settings.thresholds.pornSexyCombo}
                  onChange={(v) => updateThreshold(level, "pornSexyCombo", v)}
                />
                <ThresholdSlider
                  label="H + S combo min"
                  value={settings.thresholds.hentaiSexyCombo}
                  onChange={(v) => updateThreshold(level, "hentaiSexyCombo", v)}
                />
              </div>
            </div>

            {/* Additional thresholds */}
            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-4">Additional Thresholds</p>
              <div className="grid gap-6 md:grid-cols-2">
                <ThresholdSlider
                  label="Neutral (N)"
                  value={settings.thresholds.neutral}
                  onChange={(v) => updateThreshold(level, "neutral", v)}
                />
                <ThresholdSlider
                  label="Drawings (D)"
                  value={settings.thresholds.drawings}
                  onChange={(v) => updateThreshold(level, "drawings", v)}
                />
              </div>
            </div>

            {/* Effective rules display */}
            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-3">Effective Blocking Rules</p>
              <div className="flex flex-wrap gap-2">
                {effectiveRules.map((rule, idx) => (
                  <span key={idx} className="text-xs font-mono bg-muted px-3 py-1.5 rounded-full">
                    {rule}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Text Filter Card */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Text Filter (bog_score)</CardTitle>
            <CardDescription>
              Configure text-based content filtering • Using threshold level {thresholdLevel} for {level} mode
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Always-on categories */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">Required Filters</p>
              {TEXT_FILTER_CATEGORIES.filter(c => c.alwaysOn).map(category => (
                <TextFilterCategoryRow
                  key={category.name}
                  category={category}
                  enabled={true}
                  redMark={getCategoryRedMark(level, category.name)}
                  urlRedMark={getCategoryUrlRedMark(level, category.name)}
                  defaultRedMark={getDefaultRedMark(level, category.name)}
                  defaultUrlRedMark={getDefaultUrlRedMark(level, category.name)}
                  onToggle={() => {}}
                  onRedMarkChange={(value) => updateTextFilterRedMark(level, category.name, value)}
                  onUrlRedMarkChange={(value) => updateTextFilterUrlRedMark(level, category.name, value)}
                />
              ))}
            </div>
            
            {/* Optional categories */}
            <div className="space-y-3 pt-6 border-t">
              <p className="text-sm font-medium text-muted-foreground">Optional Filters</p>
              {TEXT_FILTER_CATEGORIES.filter(c => !c.alwaysOn).map(category => (
                <TextFilterCategoryRow
                  key={category.name}
                  category={category}
                  enabled={isCategoryEnabled(level, category)}
                  redMark={getCategoryRedMark(level, category.name)}
                  urlRedMark={getCategoryUrlRedMark(level, category.name)}
                  defaultRedMark={getDefaultRedMark(level, category.name)}
                  defaultUrlRedMark={getDefaultUrlRedMark(level, category.name)}
                  onToggle={(enabled) => updateTextFilterCategory(level, category.name, enabled)}
                  onRedMarkChange={(value) => updateTextFilterRedMark(level, category.name, value)}
                  onUrlRedMarkChange={(value) => updateTextFilterUrlRedMark(level, category.name, value)}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* JSON Preview */}
        <Accordion type="single" collapsible>
          <AccordionItem value="json" className="border rounded-lg">
            <AccordionTrigger className="px-6 hover:no-underline">
              <span className="font-medium">JSON Preview</span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6">
              <pre className="text-xs bg-muted p-6 rounded-lg overflow-auto max-h-96">
                {JSON.stringify(generateFullJson(level), null, 2)}
              </pre>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Send to Device Button */}
        <div className="flex justify-end pt-6 border-t">
          <Button 
            onClick={() => sendToDevice(level)}
            disabled={!matchedDevice || isSending}
            className="min-w-[180px]"
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send {level.charAt(0).toUpperCase() + level.slice(1)} to Device
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 p-6">
      {/* Serial Number Input Bar */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-4 px-6">
          <div className="flex items-center gap-6">
            <Label className="text-sm font-medium whitespace-nowrap">Target Device:</Label>
            <Input
              placeholder="Enter serial number (e.g., DMQVF123ABC)"
              value={targetSerial}
              onChange={(e) => setTargetSerial(e.target.value)}
              className="max-w-md font-mono text-sm uppercase"
            />
            {targetSerial && (
              matchedDevice ? (
                <Badge variant="default" className="whitespace-nowrap bg-green-600">
                  ✓ Found: {matchedDevice.serial}
                </Badge>
              ) : deviceLookup === undefined ? (
                <Badge variant="outline" className="whitespace-nowrap">
                  Searching...
                </Badge>
              ) : (
                <Badge variant="destructive" className="whitespace-nowrap">
                  Device not found
                </Badge>
              )
            )}
          </div>
        </CardContent>
      </Card>

      {/* Header with Level Tabs */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Image Filtering Playground</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Tweak NSFW thresholds and text filter settings to find the right balance
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={resetAll}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset All
          </Button>
          <Button onClick={copyAllPresets}>
            <Copy className="h-4 w-4 mr-2" />
            Copy All Presets
          </Button>
        </div>
      </div>

      {/* Level Selection Tabs */}
      <Tabs value={activeLevel} onValueChange={(v) => setActiveLevel(v as NsfwPresetLevel)} className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-12">
          <TabsTrigger value="light" className="flex items-center gap-2 data-[state=active]:bg-green-100 data-[state=active]:text-green-800">
            <ShieldCheck className="h-4 w-4" />
            <span>Light</span>
          </TabsTrigger>
          <TabsTrigger value="medium" className="flex items-center gap-2 data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-800">
            <Shield className="h-4 w-4" />
            <span>Medium</span>
          </TabsTrigger>
          <TabsTrigger value="strict" className="flex items-center gap-2 data-[state=active]:bg-red-100 data-[state=active]:text-red-800">
            <ShieldAlert className="h-4 w-4" />
            <span>Strict</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Shared Policy Settings (Collapsible) */}
      <Accordion type="single" collapsible>
        <AccordionItem value="shared" className="border rounded-lg">
          <AccordionTrigger className="px-6 hover:no-underline">
            <span className="font-medium">Shared Policy Settings</span>
          </AccordionTrigger>
          <AccordionContent className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-6">These settings apply across all levels</p>
            <div className="grid gap-6 md:grid-cols-3">
              <div className="space-y-3">
                <Label>Categories (comma-separated)</Label>
                <Input
                  placeholder="category1, category2"
                  value={state.shared.categories.join(", ")}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      shared: {
                        ...prev.shared,
                        categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                      },
                    }))
                  }
                />
              </div>
              <div className="space-y-3">
                <Label>Allowlist (domains)</Label>
                <Input
                  placeholder="example.com, site.org"
                  value={state.shared.allowlist.join(", ")}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      shared: {
                        ...prev.shared,
                        allowlist: e.target.value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
                      },
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Domains are stored lowercase</p>
              </div>
              <div className="space-y-3">
                <Label>Blocklist (domains)</Label>
                <Input
                  placeholder="blocked.com, bad.org"
                  value={state.shared.blocklist.join(", ")}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      shared: {
                        ...prev.shared,
                        blocklist: e.target.value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
                      },
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">Domains are stored lowercase</p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* NSFW Explanation Panel */}
      <NsfwExplanationPanel />

      {/* Active Level Content */}
      {renderLevelContent(activeLevel)}
    </div>
  );
};

export default BrowserSettingsSection;
